// समास (पाठ ८८): the six kinds, every example with its विग्रह (always shown), a filter by kind, and a "which kind?"
// deck of flip cards. The deck is self-study: the learner reveals the answer and rates themselves; nothing is graded
// and no XP is given. Every example carries its review badge.

import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { SamasExample, SamasType } from '@/prakrit/lib/content/grammar-examples'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { hashString, seededShuffle } from '@/prakrit/lib/grammar/questions'
import { useKeyStatuses } from '@/hooks/use-statuses'
import { SAMAS_EXAMPLES, SAMAS_TYPES, samasKey } from '@/prakrit/lib/content/grammar-examples'
import { Bi } from '@/prakrit/components/common/bi'
import { PrakritText } from '@/prakrit/components/common/prakrit-word'
import { StatusBadge, reviewBadgeKind } from '@/prakrit/components/common/status-badge'
import { SectionHeading } from '@/prakrit/components/grammar/tools/tool-page'

import {
    Check,
    ChevronLeft,
    ChevronRight,
    Info,
    RotateCcw,
    Shuffle
} from 'lucide-react'
import {
    CARD,
    Chip,
    Notice,
    SECONDARY_BUTTON,
    hiDigits
} from '@/prakrit/components/grammar/labs/lab-ui'

const TYPE_ORDER: SamasType[] = ['avyayibhav', 'tatpurush', 'karmadharay', 'dvigu', 'dvandva', 'bahuvrihi']

const SAMAS_KEYS = SAMAS_EXAMPLES.map(samasKey)

const TYPE_TONE: Record<SamasType, string> = {
    avyayibhav: 'border-success/40 bg-success-soft text-success',
    tatpurush: 'border-prakrit/35 bg-prakrit/8 text-prakrit',
    karmadharay: 'border-terracotta/45 bg-terracotta/10 text-terracotta',
    dvigu: 'border-saffron/50 bg-saffron/12 text-saffron-strong',
    dvandva: 'border-border bg-secondary text-foreground',
    bahuvrihi: 'border-prakrit/25 bg-surface-muted text-prakrit'
}

const TypePill: FC<{ type: SamasType, className?: string }> = ({ type, className }): ReactNode => (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold', TYPE_TONE[type], className)}>
        <span lang='hi' className='hindi'>{SAMAS_TYPES[type].hi}</span>
        <span aria-hidden='true' className='ex-sep opacity-60'>·</span>
        <span lang='en' data-ex='en' className='font-medium'>{SAMAS_TYPES[type].en}</span>
    </span>
)

const Vigraha: FC<{ example: SamasExample, size?: 'md' | 'sm' }> = ({ example, size = 'md' }): ReactNode => (
    <p className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5'>
        <span className='text-xs font-semibold text-muted-foreground'>
            <span lang='hi' className='hindi'>विग्रह</span>
            <span aria-hidden='true' className='ex-sep'> · </span>
            <span lang='en' data-ex='en'>analysis</span>
        </span>
        <span className={cn('pra', size === 'md' ? 'pra-md' : 'pra-sm')}>
            <PrakritText text={example.vigraha} />
        </span>
    </p>
)

interface TypeCardProps {
    type: SamasType
    count: number
    pressed: boolean
    onClick: () => void
}

const TypeCard: FC<TypeCardProps> = ({ type, count, pressed, onClick }): ReactNode => {
    const info = SAMAS_TYPES[type]
    const sample = SAMAS_EXAMPLES.find(e => e.type === type)

    return (
        <button
            type='button'
            aria-pressed={pressed}
            onClick={onClick}
            className={cn(
                'flex h-full w-full flex-col gap-2 rounded-xl border bg-card p-4 text-left shadow-card transition-colors',
                pressed ? 'border-prakrit/45 ring-2 ring-prakrit/20' : 'border-border hover:border-saffron/60'
            )}
        >
            <span className='flex flex-wrap items-baseline justify-between gap-2'>
                <span className='flex flex-wrap items-baseline gap-x-2'>
                    <span lang='hi' className='hindi text-lg font-bold'>{info.hi}</span>
                    <span lang='en' className='text-sm text-muted-foreground'>{info.en}</span>
                </span>
                <span className='text-xs text-muted-foreground'>
                    <span lang='hi' className='hindi'>{hiDigits(count)} उदाहरण</span>
                    <span className='sr-only' lang='en'> ({count} examples)</span>
                </span>
            </span>
            <Bi hi={info.note.hi} en={info.note.en} className='gap-0.5 text-sm leading-relaxed' />
            {sample && (
                <span className='mt-auto flex flex-wrap items-baseline gap-x-2 pt-1'>
                    <span lang='pra' className='pra pra-sm'>{sample.compound}</span>
                    <span lang='pra' className='font-prakrit text-sm text-muted-foreground'>= {sample.vigraha}</span>
                </span>
            )}
        </button>
    )
}

interface ExampleCardProps {
    example: SamasExample
    review: string
}

const ExampleCard: FC<ExampleCardProps> = ({ example, review }): ReactNode => (
    <li className={cn(CARD, 'flex flex-col gap-3 p-4')}>
        <div className='flex items-start justify-between gap-2'>
            <p lang='pra' className='pra pra-xl'>{example.compound}</p>
            <StatusBadge kind={reviewBadgeKind(review)} withEnglish={false} className='mt-2' />
        </div>
        <TypePill type={example.type} className='self-start' />
        <Vigraha example={example} />
        <Bi hi={example.hi} en={example.en} className='gap-0.5 text-[0.9375rem]' />
    </li>
)

const START_ORDER = seededShuffle(SAMAS_EXAMPLES.map((_, i) => i), hashString('samas-deck'))

/** "Which kind?" — flip cards for self-study. "Again" puts the card back at the end of the pile. */
const SamasDeck: FC = (): ReactNode => {
    const [queue, setQueue] = useState<number[]>(START_ORDER)
    const [flipped, setFlipped] = useState(false)
    const [known, setKnown] = useState<number[]>([])
    const [round, setRound] = useState(0)

    const total = SAMAS_EXAMPLES.length
    const index = queue[0]
    const card = index === undefined ? null : SAMAS_EXAMPLES[index]

    const reset = (order: number[]) => {
        setQueue(order)
        setKnown([])
        setFlipped(false)
    }

    const shuffle = () => {
        const next = round + 1

        setRound(next)
        reset(seededShuffle(SAMAS_EXAMPLES.map((_, i) => i), hashString(`samas-deck|${next}`)))
    }

    const rate = (knewIt: boolean) => {
        if (index === undefined) return

        setFlipped(false)

        if (knewIt) {
            setKnown(list => (list.includes(index) ? list : [...list, index]))
            setQueue(q => q.slice(1))
        }
        else {
            setQueue(q => [...q.slice(1), index])
        }
    }

    const skip = (step: 1 | -1) => {
        setFlipped(false)
        setQueue(q => (q.length < 2 ? q : step === 1 ? [...q.slice(1), q[0]] : [q[q.length - 1], ...q.slice(0, -1)]))
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowRight') {
            event.preventDefault()
            skip(1)
        }
        else if (event.key === 'ArrowLeft') {
            event.preventDefault()
            skip(-1)
        }
    }

    // Both faces share one grid cell, so the card is as tall as its taller face (no clipping at large text sizes).
    const face = 'col-start-1 row-start-1 flex flex-col items-center justify-center gap-3 rounded-2xl border px-5 py-6 text-center [backface-visibility:hidden]'

    if (!card) {
        return (
            <div className={cn(CARD, 'flex flex-col items-center gap-4 py-10 text-center')}>
                <span aria-hidden='true' className='flex size-14 items-center justify-center rounded-full bg-success-soft text-success'>
                    <Check className='size-7' />
                </span>
                <Bi
                    hi={`सभी ${hiDigits(total)} कार्ड पूरे! आपने हर समास का प्रकार पहचान लिया।`}
                    en={`All ${total} cards done! You named the kind of every compound.`}
                    className='gap-0.5 text-lg font-medium'
                />
                <button type='button' onClick={() => reset(START_ORDER)} className={SECONDARY_BUTTON}>
                    <RotateCcw className='size-4' aria-hidden='true' />
                    <Bi hi='फिर से शुरू करें' en='Start again' layout='inline' always enClassName='text-xs text-muted-foreground' />
                </button>
            </div>
        )
    }

    return (
        <div className='flex flex-col gap-5'>
            <div className='flex flex-wrap items-center justify-between gap-3'>
                <Bi
                    hi='समास पढ़िए और सोचिए: यह कौन-सा समास है? फिर कार्ड पलटिए। यह स्वयं-अभ्यास है: उत्तर आप स्वयं परखते हैं, अंक नहीं मिलते।'
                    en='Read the compound and decide which kind it is, then flip the card. This is self-study: you judge your own answer and no XP is given.'
                    className='max-w-2xl gap-0.5 text-sm'
                />
                <p className='text-sm font-medium'>
                    <span lang='hi' className='hindi'>पहचाने: {hiDigits(known.length)} / {hiDigits(total)}</span>
                    <span aria-hidden='true' className='text-muted-foreground'> · </span>
                    <span lang='en' className='text-muted-foreground'>{known.length} of {total} known</span>
                </p>
            </div>

            <div role='group' aria-label='कौन-सा समास? · Which kind of compound?' onKeyDown={onKeyDown} className='flex flex-col gap-4'>
                <button
                    type='button'
                    onClick={() => setFlipped(f => !f)}
                    aria-pressed={flipped}
                    aria-label={flipped
                        ? `${card.compound}: ${SAMAS_TYPES[card.type].hi}, विग्रह ${card.vigraha} — पलटें · flip back`
                        : `${card.compound} — यह कौन-सा समास है? उत्तर के लिए पलटें · which kind is it? Flip for the answer`}
                    className='group relative block w-full rounded-2xl text-left [perspective:1400px] focus-visible:outline-offset-4'
                >
                    <span
                        className={cn(
                            'relative grid min-h-[18rem] transition-transform duration-500 ease-out [transform-style:preserve-3d]',
                            flipped && '[transform:rotateY(180deg)]'
                        )}
                    >
                        <span aria-hidden={flipped} className={cn(face, 'border-border bg-card shadow-card')}>
                            <span className='text-xs font-semibold text-saffron-strong'>
                                <span lang='hi' className='hindi'>कौन-सा समास?</span>
                                <span aria-hidden='true'> · </span>
                                <span lang='en'>Which kind?</span>
                            </span>
                            <span lang='pra' className='pra pra-display'>{card.compound}</span>
                            <Bi hi={card.hi} en={card.en} layout='inline' className='justify-center text-base' />
                            <span className='mt-2 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-muted-foreground'>
                                <RotateCcw className='size-3.5' aria-hidden='true' />
                                <span lang='hi' className='hindi'>पलटें</span>
                                <span lang='en'>Flip</span>
                            </span>
                        </span>

                        <span aria-hidden={!flipped} className={cn(face, 'border-prakrit/30 bg-surface-muted shadow-card [transform:rotateY(180deg)]')}>
                            <span lang='pra' className='pra pra-lg'>{card.compound}</span>
                            <TypePill type={card.type} className='px-3.5 py-1 text-sm' />
                            <Bi hi={SAMAS_TYPES[card.type].note.hi} en={SAMAS_TYPES[card.type].note.en} className='max-w-md gap-0.5 text-sm' />
                            <span className='flex flex-wrap items-baseline justify-center gap-x-2'>
                                <span lang='hi' className='hindi text-xs font-semibold text-muted-foreground'>विग्रह</span>
                                <span lang='pra' className='pra pra-md'>{card.vigraha}</span>
                            </span>
                        </span>
                    </span>
                </button>

                <span aria-live='polite' className='sr-only'>
                    {flipped ? `${card.compound}: ${SAMAS_TYPES[card.type].hi} (${SAMAS_TYPES[card.type].en}). विग्रह: ${card.vigraha}` : ''}
                </span>

                <div className='flex flex-wrap items-center justify-between gap-3'>
                    {flipped ? (
                        <div className='flex flex-wrap gap-2'>
                            <button type='button' onClick={() => rate(true)} className={cn(SECONDARY_BUTTON, 'border-success/40 text-success hover:border-success hover:bg-success-soft')}>
                                <Check className='size-4' aria-hidden='true' />
                                <Bi hi='मुझे पता था' en='I knew it' layout='inline' always enClassName='text-xs opacity-80' />
                            </button>
                            <button type='button' onClick={() => rate(false)} className={SECONDARY_BUTTON}>
                                <RotateCcw className='size-4' aria-hidden='true' />
                                <Bi hi='फिर से पूछें' en='Ask me again' layout='inline' always enClassName='text-xs text-muted-foreground' />
                            </button>
                        </div>
                    ) : (
                        <div className='flex flex-wrap gap-2'>
                            <button type='button' onClick={() => skip(-1)} disabled={queue.length < 2} className={SECONDARY_BUTTON}>
                                <ChevronLeft className='size-4' aria-hidden='true' />
                                <Bi hi='पिछला' en='Previous' layout='inline' always enClassName='text-xs text-muted-foreground' />
                            </button>
                            <button type='button' onClick={() => skip(1)} disabled={queue.length < 2} className={SECONDARY_BUTTON}>
                                <Bi hi='अगला' en='Next' layout='inline' always enClassName='text-xs text-muted-foreground' />
                                <ChevronRight className='size-4' aria-hidden='true' />
                            </button>
                        </div>
                    )}
                    <button type='button' onClick={shuffle} className={SECONDARY_BUTTON}>
                        <Shuffle className='size-4' aria-hidden='true' />
                        <Bi hi='नए क्रम में' en='Shuffle' layout='inline' always enClassName='text-xs text-muted-foreground' />
                    </button>
                </div>
            </div>

            <div aria-hidden='true' className='flex flex-wrap justify-center gap-1.5'>
                {SAMAS_EXAMPLES.map((e, i) => (
                    <span key={e.id} className={cn('h-1.5 rounded-full transition-all', i === index ? 'w-6 bg-saffron' : known.includes(i) ? 'w-3 bg-success/70' : 'w-3 bg-border')} />
                ))}
            </div>
        </div>
    )
}

export const SamasLab: FC = (): ReactNode => {
    const status = useKeyStatuses(SAMAS_KEYS)
    const [filter, setFilter] = useState<SamasType | 'all'>('all')
    const shown = filter === 'all' ? SAMAS_EXAMPLES : SAMAS_EXAMPLES.filter(e => e.type === filter)

    const toggle = (type: SamasType) => setFilter(current => (current === type ? 'all' : type))

    return (
        <>
            <section aria-labelledby='samas-types' className='flex flex-col gap-5'>
                <SectionHeading id='samas-types' hi='समास के छह प्रकार' en='The six kinds of compound' rule='पाठ ८८'>
                    <Bi
                        hi='दो या अधिक शब्द मिलकर एक शब्द बनाते हैं: यह समास है। समास को खोलकर उसके शब्द अलग लिखना विग्रह कहलाता है। किसी प्रकार पर टैप करें तो नीचे केवल उसी के उदाहरण दिखेंगे।'
                        en='Two or more words joined into one word make a compound (समास). Writing its words out separately is its विग्रह (analysis). Tap a kind to see only its examples below.'
                        className='max-w-3xl gap-0.5 text-sm'
                    />
                </SectionHeading>
                <ul className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                    {TYPE_ORDER.map(type => (
                        <li key={type}>
                            <TypeCard
                                type={type}
                                count={SAMAS_EXAMPLES.filter(e => e.type === type).length}
                                pressed={filter === type}
                                onClick={() => toggle(type)}
                            />
                        </li>
                    ))}
                </ul>
            </section>

            <section aria-labelledby='samas-examples' className='flex flex-col gap-5'>
                <SectionHeading id='samas-examples' hi='उदाहरण और विग्रह' en='Examples with their analysis' />
                <div role='group' aria-label='प्रकार से छाँटें · Filter by kind' className='flex flex-wrap gap-2'>
                    <Chip pressed={filter === 'all'} onClick={() => setFilter('all')}>
                        <Bi hi='सभी' en='All' layout='inline' always enClassName='text-xs text-muted-foreground' />
                        <span className='text-xs text-muted-foreground'>{hiDigits(SAMAS_EXAMPLES.length)}</span>
                    </Chip>
                    {TYPE_ORDER.map(type => (
                        <Chip key={type} pressed={filter === type} onClick={() => setFilter(type)}>
                            <span lang='hi' className='hindi'>{SAMAS_TYPES[type].hi}</span>
                            <span lang='en' className='text-xs text-muted-foreground'>{SAMAS_TYPES[type].en}</span>
                        </Chip>
                    ))}
                </div>
                <p aria-live='polite' className='sr-only'>
                    {`${shown.length} उदाहरण · ${shown.length} examples`}
                </p>
                <ul className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                    {shown.map(e => <ExampleCard key={e.id} example={e} review={status(samasKey(e))} />)}
                </ul>
                <p className='text-xs text-muted-foreground'>
                    <Bi
                        hi='विग्रह के शब्दों पर टैप करें: जो रूप हमारी तालिकाओं में हैं, उनका विश्लेषण खुलेगा।'
                        en='Tap the words of an analysis: forms that are in our tables open their full analysis.'
                    />
                </p>
            </section>

            <section aria-labelledby='samas-deck' className='flex flex-col gap-5'>
                <SectionHeading id='samas-deck' hi='कौन-सा समास?' en='Which kind? A self-check' />
                <SamasDeck />
                <Notice icon={<Info className='size-4 text-saffron-strong' />}>
                    <Bi
                        hi='ये उदाहरण पुस्तक के पाठ ८८ से हैं; हर उदाहरण पर दिखाया गया है कि शिक्षक ने उसकी जाँच की है या नहीं। यह स्वयं-अभ्यास है, इसलिए यहाँ अंक नहीं मिलते।'
                        en="These examples come from पाठ ८८ of the course book; each one shows whether a teacher has checked it yet. This is self-study, so no XP is given here."
                        className='gap-0.5'
                    />
                </Notice>
            </section>
        </>
    )
}
